import { hapticFeedback } from "@telegram-apps/sdk-react";
import { Button } from "@telegram-apps/telegram-ui";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { useSnackbar } from "~/Provider";
import {
    fetchSuggestionTokens,
    postBatchAddFavoriteToken,
} from "~/api/endpoints";
import { IconCheckOk } from "~/components/Iconfont/Iconfont";
import { TokenBadge } from "~/components/TokenBadge/TokenBadge";
import { TokenIconSize } from "~/components/TokenIcon";
import { TokenBase } from "~/types";

type FavoriteSuggestionProps = {
    afterAdded: () => void;
};

const FavoriteSuggestion: React.FC<FavoriteSuggestionProps> = ({
    afterAdded,
}) => {
    const { t } = useTranslation();

    const fetcher = () =>
        fetchSuggestionTokens().then((res) => res.data.list ?? []);

    const { data } = useSWR("fetchSuggestionTokens", fetcher);

    const [recommandList, setRecommandList] = useState<
        Array<TokenBase & { isSelect?: boolean }>
    >([]);

    useEffect(
        () =>
            setRecommandList(
                (data ?? []).map((item) => ({ ...item, isSelect: true }))
            ),
        [data]
    );

    const handleSelectToken = (token: TokenBase & { isSelect?: boolean }) => {
        hapticFeedback.impactOccurred("light");
        setRecommandList((oldList) => {
            return oldList.map((item) => {
                const isSameToken =
                    String(item.chain_id) === String(token.chain_id) &&
                    item.contract === token.contract;
                return isSameToken
                    ? { ...item, isSelect: !token.isSelect }
                    : item;
            });
        });
    };

    const snapbar = useSnackbar();
    const [addLoading, setAddLoading] = useState(false);
    const handleAddToFavorite = () => {
        if (addLoading) return;
        setAddLoading(true);

        hapticFeedback.impactOccurred("light");

        const selectedTokens = recommandList
            .filter((item) => item.isSelect)
            .map((item) => ({
                chain_id: String(item.chain_id),
                contract: item.contract,
            }));

        postBatchAddFavoriteToken(selectedTokens)
            .then(() => {
                afterAdded();
            })
            .catch((err) => {
                snapbar.error(
                    "Can not add favorite token, Please try again later!",
                    err
                );
            })
            .finally(() => {
                setAddLoading(false);
            });
    };

    if (recommandList.length === 0) return;

    const isSelectNone = recommandList.every((item) => item.isSelect === false);

    return (
        <div className="px-4 flex flex-wrap gap-2 mt-3">
            {recommandList.map((token) => {
                return (
                    <div
                        className={clsx(
                            "bg-bg_1 rounded-xl px-2 py-4 flex items-center cursor-pointer",
                            !token.isSelect && "opacity-60"
                        )}
                        style={{ width: "calc(50vw - 1.25rem)" }}
                        onClick={() => handleSelectToken(token)}
                    >
                        <div className="flex-none mr-1.5">
                            <TokenBadge
                                size={TokenIconSize.small}
                                tailIconSize={TokenIconSize.mini21}
                                icon={token.logo}
                                symbol={token.symbol}
                                tokenIconBorder={true}
                                chainIcon={token.chain_logo}
                                chainName={token.chain_name}
                            />
                        </div>
                        <div className="flex-1 flex flex-col justify-center h-full">
                            <span className="text-body1 text-text_1 font-Semibold mb-0.5 h-6 flex items-center">
                                {token.symbol.toUpperCase()}
                            </span>
                            <span className="text-body4 text-text_2">
                                {token.chain_name}
                            </span>
                        </div>
                        <div className="flex-none p-2">
                            <div
                                className={clsx(
                                    "flex items-center justify-center w-[18px] h-[18px] rounded-full cursor-pointer",
                                    token.isSelect
                                        ? "bg-text_brand"
                                        : "bg-text_3"
                                )}
                            >
                                <IconCheckOk className="text-bg_0" />
                            </div>
                        </div>
                    </div>
                );
            })}
            <div className="flex w-full justify-center my-10">
                <Button
                    style={
                        {
                            width: 220,
                            height: 54,
                            borderRadius: 12,
                            backgroundColor: "var(--color_button_brand)",
                            color: "var(--White)",
                            "--tgui--font_weight--accent2": "600",
                            "--tgui--subheadline2--font_size": "18px",
                            "--tgui--subheadline2--line_height": "26px",
                        } as React.CSSProperties
                    }
                    onClick={handleAddToFavorite}
                    loading={addLoading}
                    disabled={isSelectNone}
                >
                    {t("add_to_favorite")}
                </Button>
            </div>
        </div>
    );
};

export default FavoriteSuggestion;
